import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "react-feather";
import { getStorageValues } from "../../../shared/CommonFunctions";
import { getApiBaseUrl } from "../../../utils/apiConfig";
import { logAuditEvent } from "../../../utils/auditLogger";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { Pie } from "react-chartjs-2";
import { fetchEnquiryList } from "../../../utils/enquiryApi";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);
const pieColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#8dd1e1', '#a4de6c', '#d0ed57', '#ffc0cb'];

function Dashboard() {
  const moduleName = "Dashboard";
  const [summary, setSummary] = useState({
    PendingEnquiryCount: 0,
    SoldBikeCountCurrentMonth: 0,
    TotalStockCount: 0,
    UpcomingDeliveryCount: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [salesByMonth, setSalesByMonth] = useState([]);
  const [enquiryByArea, setEnquiryByArea] = useState([]);
  const [topSellingBikes, setTopSellingBikes] = useState([]);
  const [, setLoadingCharts] = useState(false);
  const [getEnquiryList, setEnquiryList] = useState([]);
  const [followupReminders, setFollowupReminders] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [upcomingDeliveries, setUpcomingDeliveries] = useState([]);
  const [activePendingTab, setActivePendingTab] = useState("rto"); // 'rto' | 'finance'
  const [pendingRtoRows, setPendingRtoRows] = useState([]);
  const [pendingFinanceRows, setPendingFinanceRows] = useState([]);

  const barData = {
    labels: salesByMonth.map(item => item.month),
    datasets: [
      {
        label: "Sold Bikes",
        data: salesByMonth.map(item => item.count),
        backgroundColor: "#8884d8",
      },
    ],
  };
  const pieData = {
    labels: enquiryByArea.map(item => item.area),
    datasets: [
      {
        data: enquiryByArea.map(item => item.count),
        backgroundColor: pieColors,
      },
    ],
  };
  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
  };
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
  };
  const fetchDashboardSummary = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(getApiBaseUrl() + "/dashboard/app2summary", {
        params: {
          UserId: getStorageValues("userId"),
        },
      });

      if (response.status === 200 && response?.data?.data) {
        setSummary({
          PendingEnquiryCount: Number(response.data.data.PendingEnquiryCount || 0),
          SoldBikeCountCurrentMonth: Number(response.data.data.SoldBikeCountCurrentMonth || 0),
          TotalStockCount: Number(response.data.data.TotalStockCount || 0),
          UpcomingDeliveryCount: Number(response.data.data.UpcomingDeliveryCount || 0),
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };
  const fetchEnquiries = async () => {
      try {
        const rows = await fetchEnquiryList({ userId: getStorageValues('userId') });
        setEnquiryList(rows);
      } catch (error) {
        console.log(error);
      }
  };

  const fetchFollowupReminders = async () => {
    try {
      const response = await axios.get(getApiBaseUrl() + "/dashboard/app2followupreminders", {
        params: { UserId: getStorageValues("userId") },
      });
      if (response.status === 200) {
        const payload = response?.data?.data;
        const normalized = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.[0])
            ? payload[0]
            : [];
        setFollowupReminders(normalized);
      }
    } catch (error) {
      console.log(error);
      setFollowupReminders([]);
    }
  };

  const fetchPendingPayments = async () => {
    try {
      const response = await axios.get(getApiBaseUrl() + "/salesregister/getPendingPaymentList", {
        params: { UserId: getStorageValues("userId") },
      });
      if (response.status === 200) {
        const payload = response?.data?.data;
        const normalized = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.[0])
            ? payload[0]
            : [];
        setPendingPayments(normalized);
      }
    } catch (error) {
      console.log(error);
      setPendingPayments([]);
    }
  };

  const fetchUpcomingDeliveries = async () => {
    try {
      const response = await axios.get(getApiBaseUrl() + "/handoverregister/eligible", {
        params: { UserId: getStorageValues("userId") },
      });
      if (response.status === 200) {
        const payload = response?.data?.data;
        const normalized = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.[0])
            ? payload[0]
            : [];
        setUpcomingDeliveries(normalized);
      }
    } catch (error) {
      console.log(error);
      setUpcomingDeliveries([]);
    }
  };

  const isCompletedStatus = (status) => {
    const s = String(status || "").trim().toLowerCase();
    return s === "complete" || s === "completed";
  };

  const fetchRtoRegisters = async () => {
    try {
      const response = await axios.get(getApiBaseUrl() + "/rtoregister/getall", {
        params: { UserId: getStorageValues("userId") },
      });
      if (response.status === 200) {
        const payload = response?.data?.data;
        const normalized = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.[0])
            ? payload[0]
            : [];
        setPendingRtoRows(normalized.filter((x) => !isCompletedStatus(x?.Status)));
      }
    } catch (error) {
      console.log(error);
      setPendingRtoRows([]);
    }
  };

  const fetchFinanceRegisters = async () => {
    try {
      const response = await axios.get(getApiBaseUrl() + "/financeregister/getall", {
        params: { UserId: getStorageValues("userId") },
      });
      if (response.status === 200) {
        const payload = response?.data?.data;
        const normalized = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.[0])
            ? payload[0]
            : [];
        setPendingFinanceRows(normalized.filter((x) => !isCompletedStatus(x?.Status)));
      }
    } catch (error) {
      console.log(error);
      setPendingFinanceRows([]);
    }
  };

  useEffect(() => {
    logAuditEvent(moduleName, "Accessed Module", "Accessed Dashboard (Application 2)");
    fetchDashboardSummary();
    fetchEnquiries();
    fetchFollowupReminders();
    fetchPendingPayments();
    fetchUpcomingDeliveries();
    fetchRtoRegisters();
    fetchFinanceRegisters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleName]);

  useEffect(() => {
    fetchCharts(selectedYear);
  }, [moduleName, selectedYear]);

  const fetchCharts = async (year) => {
    setLoadingCharts(true);
    try {
      const [salesRes, areaRes, topSellingRes] = await Promise.all([
        axios.get(getApiBaseUrl() + "/dashboard/app2salesbymonth", { params: { year } }),
        axios.get(getApiBaseUrl() + "/dashboard/app2enquirybyarea"),
        axios.get(getApiBaseUrl() + "/dashboard/app2topsellingbikes", { params: { year } }),
      ]);
      setSalesByMonth(Array.isArray(salesRes.data.data) ? salesRes.data.data : []);
      setEnquiryByArea(Array.isArray(areaRes.data.data) ? areaRes.data.data : []);
      setTopSellingBikes(Array.isArray(topSellingRes.data.data) ? topSellingRes.data.data : []);
    } catch (err) {
      setSalesByMonth([]);
      setEnquiryByArea([]);
      setTopSellingBikes([]);
    } finally {
      setLoadingCharts(false);
    }
  };

  // Generate last 5 years for dropdown
  const yearOptions = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  // Pie chart colors
  

  const summaryCards = [
    {
      title: "Pending Enquiry",
      value: summary.PendingEnquiryCount,
      route: "/home/enquiry",
      icon: <Icons.HelpCircle size={22} />,
    },
    {
      title: "Sold Bikes (Current Month)",
      value: summary.SoldBikeCountCurrentMonth,
      route: "/home/sales-register",
      icon: <Icons.ShoppingCart size={22} />,
    },
    {
      title: "Total Stock",
      value: summary.TotalStockCount,
      route: "/home/purchase-register",
      icon: <Icons.Package size={22} />,
    },
    {
      title: "Upcoming Delivery",
      value: summary.UpcomingDeliveryCount,
      route: "/home/handover-register",
      icon: <Icons.Truck size={22} />,
    },
  ];

  // quickLinks removed (unused)

  return (
    <>
      <div className="row">
        {summaryCards.map((card, index) => (
          <div className="col-xl-3 col-sm-6 col-12" key={card.title}>
            <div className={"dash-count das" + index}>
              <div className="dash-counts">
                <h4>
                  <Link to={card.route}>
                    <b>{isLoading ? "..." : card.value}</b>
                  </Link>
                </h4>
                <h5>
                  <Link to={card.route}>
                    <b>{card.title}</b>
                  </Link>
                </h5>
              </div>
              <div className="dash-imgs">
                {card.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row mt-4">
        <div className="col-xl-6 col-sm-12 col-12 d-flex">
          <div className="card flex-fill default-cover mb-4 w-100">
            <div className="card-header d-flex justify-content-between align-items-center bg-tableheader">
              <h4 className="card-title mb-0">Monthly Sold Bikes ({selectedYear})</h4>
              <select
                className="form-select w-auto ms-1"
                value={selectedYear}
                onChange={e => setSelectedYear(Number(e.target.value))}
                style={{ minWidth: 100, height: "24px", fontSize: "8px", fontWeight: "bold" }}
              >
                {yearOptions.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className="card-body">
              <div style={{ height: "260px", position: "relative" }}>
                <Bar data={barData} options={barOptions} />
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-6 col-sm-12 col-12 d-flex">
          <div className="card flex-fill default-cover mb-4 w-100">
            <div className="card-header d-flex justify-content-between align-items-center bg-tableheader">
              <h4 className="card-title mb-0">Area-wise Enquiries</h4>
            </div>
            <div className="card-body">
              <div style={{ height: "260px", position: "relative" }}>
                <Pie data={pieData} options={pieOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        
      </div>

      <div className="row">
        {(() => {
          // ✅ 2. Loop through each enquiry and render the latest 5 tasks
            const enquiries = getEnquiryList
              .filter(enq => enq.IsSold === 0)
              .slice(0, 5); // get latest 5
    
            return (
              <>
                <div className="col-sm-12 col-md-12 col-xl-6 d-flex">
                  <div className="card flex-fill default-cover w-100 mb-4">
                    <div className="card-header d-flex justify-content-between align-items-center bg-tableheader">
                      <h4 className="card-title mb-0">Pending Enquries</h4>
                      <div className="dropdown">
                        <Link
                          to="/home/enquiry"
                          className="view-all d-flex align-items-center alink-white"
                        >
                          View All
                          <span className="ps-2 d-flex align-items-center">
                            <Icons.ArrowRight size={17} />
                          </span>
                        </Link>
                      </div>
                    </div>
      
                    <div className="card-body">
                      <div className="table-responsive">
                        <table className="table table-nowrap table-hover">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Key</th>
                              <th>Customer Name</th>
                              <th>Mobile No</th>
                              <th>Area</th>
                              <th>Enquire For</th>
                            </tr>
                          </thead>
                          <tbody>
                            {enquiries.map((obj, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{obj.EnquiryNumber}</td>
                                <td>{obj.CustomerName}</td>
                                <td>{obj.MobileNo}</td>
                                <td>{obj.Area}</td>
                                <td className="ellipsis-cell" title={obj.EnquireFor}>
                                  {obj.EnquireFor}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-sm-12 col-md-12 col-xl-6 d-flex">
                  <div className="card flex-fill default-cover w-100 mb-4">
                    <div className="card-header d-flex justify-content-between align-items-center bg-tableheader">
                      <h4 className="card-title mb-0">Follow-up Reminder</h4>
                      <div className="dropdown">
                        <span className="view-all d-flex align-items-center alink-white" title="Upcoming follow-ups in next 4 days">
                          Next 4 days
                        </span>
                      </div>
                    </div>

                    <div className="card-body">
                      <div className="table-responsive dataview">
                        <table className="table dashboard-recent-products table-hover">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Type</th>
                              <th>Key</th>
                              <th>Customer</th>
                              <th>Follow-up Date</th>
                              <th>Remark</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(followupReminders || []).slice(0, 5).length > 0 ? (
                              (followupReminders || []).slice(0, 5).map((obj, index) => {
                                const type = String(obj?.SourceType || "");
                                const entityId = obj?.EntityId;
                                const key = obj?.FollowupKey;
                                const customer = obj?.CustomerName;
                                const date = obj?.FollowupDate;
                                const remark = obj?.FollowupRemarks;

                                const link =
                                  type === "Enquiry"
                                    ? { to: "/home/enquiry", state: { DefaultEnquiryId: entityId, ExpandFollowup: true } }
                                    : type === "Finance"
                                      ? { to: "/home/finance-register", state: { DefaultFinanceRegisterId: entityId, ExpandFollowup: true } }
                                      : { to: "/home/payment-followup", state: { DefaultSalesRegisterId: entityId, ExpandFollowup: true } };

                                return (
                                  <tr key={`${type}-${entityId}-${index}`}>
                                    <td>{index + 1}</td>
                                    <td>{type}</td>
                                    <td>
                                      <Link to={link.to} state={link.state} title="Click to open follow-up">
                                        {key || "-"}
                                      </Link>
                                    </td>
                                    <td>{customer || "-"}</td>
                                    <td>{date || "-"}</td>
                                    <td className="ellipsis-cell" title={remark || ""}>
                                      {remark || "-"}
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center">
                                  No upcoming follow-ups found!!
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            );
        })()}
      </div>

      <div className="row">
        <div className="col-sm-12 col-md-12 col-xl-6 d-flex">
          <div className="card flex-fill default-cover w-100 mb-4">
            <div className="card-header d-flex justify-content-between align-items-center bg-tableheader">
              <div className="d-flex align-items-center">
                <h4 className="card-title mb-0 me-3">Pending Registers</h4>
                <div className="btn-group btn-group-sm" role="group" aria-label="Pending tabs">
                  <button
                    type="button"
                    className={`btn ${activePendingTab === "rto" ? "btn-light" : "btn-outline-light"}`}
                    onClick={() => setActivePendingTab("rto")}
                  >
                    RTO
                  </button>
                  <button
                    type="button"
                    className={`btn ${activePendingTab === "finance" ? "btn-light" : "btn-outline-light"}`}
                    onClick={() => setActivePendingTab("finance")}
                  >
                    Finance
                  </button>
                </div>
              </div>

              <div className="dropdown">
                <Link
                  to={activePendingTab === "rto" ? "/home/rto" : "/home/finance-register"}
                  className="view-all d-flex align-items-center alink-white"
                >
                  View All
                  <span className="ps-2 d-flex align-items-center">
                    <Icons.ArrowRight size={17} />
                  </span>
                </Link>
              </div>
            </div>

            <div className="card-body">
              <div className="table-responsive dataview">
                <table className="table dashboard-recent-products table-hover">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Sales #</th>
                      <th>Customer</th>
                      <th>Mobile</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(activePendingTab === "rto" ? pendingRtoRows : pendingFinanceRows).slice(0, 5).length > 0 ? (
                      (activePendingTab === "rto" ? pendingRtoRows : pendingFinanceRows).slice(0, 5).map((row, index) => {
                        const to = activePendingTab === "rto" ? "/home/rto" : "/home/finance-register";
                        const state =
                          activePendingTab === "rto"
                            ? { DefaultRTORegisterId: row?.RTORegisterId }
                            : { DefaultFinanceRegisterId: row?.FinanceRegisterId, ExpandFollowup: true };
                        const mobile = activePendingTab === "rto" ? row?.MobileNumber : row?.MobileNo;
                        return (
                          <tr key={`${activePendingTab}-${row?.RTORegisterId || row?.FinanceRegisterId || index}`}>
                            <td>{index + 1}</td>
                            <td>
                              <Link to={to} state={state} title="Open record">
                                {row?.SalesNumber || "-"}
                              </Link>
                            </td>
                            <td>{row?.CustomerName || "-"}</td>
                            <td>{mobile || "-"}</td>
                            <td>{row?.Status || "-"}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">
                          No pending records found!!
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-sm-12 col-md-12 col-xl-6 d-flex">
          <div className="card flex-fill default-cover w-100 mb-4">
            <div className="card-header d-flex justify-content-between align-items-center bg-tableheader">
              <h4 className="card-title mb-0">Pending Payments</h4>
              <div className="dropdown">
                <Link
                  to="/home/payment-followup"
                  className="view-all d-flex align-items-center alink-white"
                >
                  View All
                  <span className="ps-2 d-flex align-items-center">
                    <Icons.ArrowRight size={17} />
                  </span>
                </Link>
              </div>
            </div>

            <div className="card-body">
              <div className="table-responsive dataview">
                <table className="table dashboard-recent-products table-hover">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Sales #</th>
                      <th>Customer</th>
                      <th>Mobile</th>
                      <th style={{ textAlign: "right" }}>Pending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(pendingPayments || []).slice(0, 5).length > 0 ? (
                      (pendingPayments || []).slice(0, 5).map((row, index) => {
                        const pending = Number(row?.PendingAmount || 0);
                        const pendingLabel = pending.toLocaleString("en-IN", {
                          style: "currency",
                          currency: "INR",
                        });
                        return (
                          <tr key={`${row?.SalesRegisterId || index}`}>
                            <td>{index + 1}</td>
                            <td>
                              <Link
                                to="/home/payment-followup"
                                state={{ DefaultSalesRegisterId: row?.SalesRegisterId, ExpandFollowup: true }}
                                title="Open payment follow-up"
                              >
                                {row?.SalesNumber || "-"}
                              </Link>
                            </td>
                            <td>{row?.CustomerName || "-"}</td>
                            <td>{row?.MobileNumber || "-"}</td>
                            <td style={{ textAlign: "right" }}>{pendingLabel}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">
                          No pending payments found!!
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-12 col-xl-6 d-flex">
          <div className="card flex-fill default-cover w-100 mb-4">
            <div className="card-header d-flex justify-content-between align-items-center bg-tableheader">
              <h4 className="card-title mb-0">Upcoming Delivery</h4>
              <div className="dropdown">
                <Link
                  to="/home/handover-register"
                  className="view-all d-flex align-items-center alink-white"
                >
                  View All
                  <span className="ps-2 d-flex align-items-center">
                    <Icons.ArrowRight size={17} />
                  </span>
                </Link>
              </div>
            </div>

            <div className="card-body">
              <div className="table-responsive dataview">
                <table className="table dashboard-recent-products table-hover">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Sales #</th>
                      <th>Customer</th>
                      <th>Mobile</th>
                      <th>Payment Mode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(upcomingDeliveries || []).slice(0, 5).length > 0 ? (
                      (upcomingDeliveries || []).slice(0, 5).map((row, index) => {
                        return (
                          <tr key={`${row?.SalesRegisterId || index}`}>
                            <td>{index + 1}</td>
                            <td>
                              <Link
                                to="/home/handover-register"
                                state={{ DefaultSalesRegisterId: row?.SalesRegisterId }}
                                title="Open handover register"
                              >
                                {row?.SalesNumber || "-"}
                              </Link>
                            </td>
                            <td>{row?.CustomerName || "-"}</td>
                            <td>{row?.MobileNumber || "-"}</td>
                            <td>{row?.PaymentTypeSummary || "-"}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">
                          No upcoming deliveries found!!
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-6 col-sm-12 col-12 d-flex">
          <div className="card flex-fill default-cover mb-4 w-100">
            <div className="card-header d-flex justify-content-between align-items-center bg-tableheader">
              <h4 className="card-title mb-0">Most Selling Bikes (Top 5) ({selectedYear})</h4>
            </div>
            <div className="card-body">
              <div className="table-responsive dataview">
                <table className="table dashboard-recent-products table-hover">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Bike Model</th>
                      <th style={{ textAlign: "right" }}>Sold</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(topSellingBikes || []).length > 0 ? (
                      (topSellingBikes || []).slice(0, 5).map((row, index) => (
                        <tr key={`${row?.Manufacturer || ""}-${row?.ModelName || ""}-${index}`}>
                          <td>{index + 1}</td>
                          <td>{`${row?.Manufacturer || ""} ${row?.ModelName || ""}`.trim() || "-"}</td>
                          <td style={{ textAlign: "right" }}>{row?.SoldCount ?? 0}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center">
                          No data found!!
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      
    </>
  );
}

export default Dashboard;
